import { Inject, Injectable, Optional } from '@angular/core';

import { IBetslipState } from '../../../../../base/store/state';
import { isLinear } from '../../../../../base/utils/linear-utils';
import { BetslipType } from '../../../../../core/betslip-type';
import { BetslipPick } from '../../../../../core/picks/betslip-pick';
import { ResultError } from '../../../errors/result/result-error';
import {
    BETSTATION_REWARD_TOKENS_PICK_VALIDATORS,
    CONTEXT_PICK_VALIDATORS,
    PURE_LINEAR_PICK_VALIDATORS,
    PURE_PICK_VALIDATORS,
    TEASER_PICK_VALIDATORS,
} from '../../betslip-validation-injection-tokens';
import { PickContextGeneratorService } from '../../generators/context/pick-context-generator.service';
import { RewardTokensContextGeneratorService } from '../../generators/context/reward-tokens-context-generator.service';
import { TypeContextGeneratorService } from '../../generators/context/type-context-generator.service';
import {
    IBetstationRewardTokensPickValidator,
    IContextPickValidator,
    IPureLinearPickValidator,
    IPurePickValidator,
    ITeaserBetPickValidator,
} from '../../validators/picks/validators';

@Injectable()
export class PickValidationComposerService {
    constructor(
        @Inject(PURE_PICK_VALIDATORS) private purePickValidators: IPurePickValidator<ResultError>[],
        @Inject(PURE_LINEAR_PICK_VALIDATORS) private pureLinearPickValidators: IPureLinearPickValidator<ResultError>[],
        @Inject(CONTEXT_PICK_VALIDATORS) private contextPickValidators: IContextPickValidator<ResultError>[],
        private pickContextGenerator: PickContextGeneratorService,
        private typeContextGenerator: TypeContextGeneratorService,
        private rewardTokenContextGenerator: RewardTokensContextGeneratorService,
        @Optional() @Inject(TEASER_PICK_VALIDATORS) private teaserPickValidators?: ITeaserBetPickValidator<ResultError>[],
        @Optional()
        @Inject(BETSTATION_REWARD_TOKENS_PICK_VALIDATORS)
        private betstationPickValidators?: IBetstationRewardTokensPickValidator<ResultError>[],
    ) {}

    validatePick(pick: BetslipPick, type: BetslipType, state: IBetslipState): ResultError[] {
        const context = this.pickContextGenerator.generateContext(pick, state);

        const pureErrors = this.purePickValidators.flatMap((validator) => validator.validate(pick));

        const pureLinearErrors = isLinear(state) ? this.pureLinearPickValidators.flatMap((validator) => validator.validate(pick)) : [];

        const contextErrors = this.contextPickValidators.flatMap((validator) => validator.validate(pick, context));

        const typeErrors = this.validatePickForType(pick, type, state);

        const betstationErrors = this.validatePickForBetstation(pick, state);

        return [...pureErrors, ...pureLinearErrors, ...contextErrors, ...typeErrors, ...betstationErrors];
    }

    private validatePickForType(pick: BetslipPick, type: BetslipType, state: IBetslipState): ResultError[] {
        if (type !== BetslipType.Teaser) {
            return [];
        }

        const context = this.typeContextGenerator.generateTeaserBetPickContext(state.types.teaserBet);

        return this.teaserPickValidators?.flatMap((validator) => validator.validate(pick, context)) ?? [];
    }

    private validatePickForBetstation(pick: BetslipPick, state: IBetslipState): ResultError[] {
        if (!this.betstationPickValidators?.length) {
            return [];
        }

        const context = this.rewardTokenContextGenerator.generateBetStationPickContext(state);

        return this.betstationPickValidators?.flatMap((validator) => validator.validate(pick, context));
    }
}
